import { useCallback, useEffect, useRef, useState } from 'react';
import { getAlimentadorIdsByComunaId, getUncatchEndpointData } from '../utils/request';
import { DEFAULT_REGION_NOMBRE, DEFAULT_COMUNA_NOMBRE } from '../constants/mapDefaults';

export type SelectOption = { value: string; name: string };

export type MapRegionComunaState = {
  regiones: SelectOption[];
  comunas: SelectOption[];
  selectedRegion: string;
  selectedComuna: string;
  localMapCenter: { lng: number; lat: number } | null;
  alimentadorIds: number[];
  loading: boolean;
};

export type MapRegionComunaActions = {
  setSelectedRegion: (id: string) => void;
  setSelectedComuna: (id: string) => void;
};

/**
 * Hook que replica exactamente el flujo región/comuna de PublicMapPage
 * y además resuelve los alimentadorIds de la comuna seleccionada.
 * Los datos del mapa (capacidad nodal, etc.) deben cargarse sólo cuando
 * selectedComuna !== '' y alimentadorIds.length > 0.
 */
export function useMapRegionComuna(): MapRegionComunaState & MapRegionComunaActions {
  const [regiones, setRegiones] = useState<SelectOption[]>([]);
  const [comunas, setComunas] = useState<SelectOption[]>([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedComuna, setSelectedComuna] = useState('');
  const [localMapCenter, setLocalMapCenter] = useState<{ lng: number; lat: number } | null>(null);
  const [alimentadorIds, setAlimentadorIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const isInitialMount = useRef(true);

  // Carga inicial: regiones y selección por defecto
  useEffect(() => {
    (async () => {
      try {
        const regionesReq: { regionId: string; regionNombre: string }[] =
          await getUncatchEndpointData({ endpoint: 'region' });
        if (regionesReq.length > 0) {
          setRegiones(regionesReq.map((r) => ({ value: r.regionId, name: r.regionNombre })));
          const def = regionesReq.find((r) => r.regionNombre === DEFAULT_REGION_NOMBRE);
          if (def) {
            setSelectedRegion(def.regionId);
          } else {
            console.warn(`Región por defecto "${DEFAULT_REGION_NOMBRE}" no encontrada`);
            isInitialMount.current = false;
            setLoading(false);
          }
        } else {
          isInitialMount.current = false;
          setLoading(false);
        }
      } catch (e) {
        console.error(e);
        isInitialMount.current = false;
        setLoading(false);
      }
    })();
  }, []);

  // Cambio de región → cargar comunas
  useEffect(() => {
    if (!selectedRegion) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setSelectedComuna('');
      setAlimentadorIds([]);
      try {
        const region = await getUncatchEndpointData({
          endpoint: 'region/getRegion',
          query: { regionId: selectedRegion }
        });
        if (!cancelled && region.length > 0) {
          setLocalMapCenter({ lng: region[0].longitud, lat: region[0].latitud });
        }
        const comunasReq: { comunaId: string; comunaNombre: string }[] =
          await getUncatchEndpointData({ endpoint: 'comuna', query: { regionId: selectedRegion } });
        if (cancelled) return;
        if (comunasReq.length > 0) {
          setComunas(comunasReq.map((c) => ({ value: c.comunaId, name: c.comunaNombre })));
          if (isInitialMount.current) {
            const def = comunasReq.find((c) => c.comunaNombre === DEFAULT_COMUNA_NOMBRE);
            if (def) {
              setSelectedComuna(def.comunaId);
            }
            isInitialMount.current = false;
          }
        } else {
          setComunas([]);
          if (isInitialMount.current) isInitialMount.current = false;
        }
      } catch (e) {
        console.error(e);
        if (isInitialMount.current) isInitialMount.current = false;
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedRegion]);

  // Cambio de comuna → obtener centro + alimentadorIds (mismo patrón que PublicMapPage)
  useEffect(() => {
    if (!selectedComuna) {
      setAlimentadorIds([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [comunaRes, ids] = await Promise.all([
          getUncatchEndpointData({ endpoint: 'comuna/getComuna', query: { comunaId: selectedComuna } }),
          getAlimentadorIdsByComunaId(selectedComuna)
        ]);
        if (cancelled) return;
        if (Array.isArray(comunaRes) && comunaRes.length > 0) {
          setLocalMapCenter({ lng: comunaRes[0].longitud, lat: comunaRes[0].latitud });
        }
        setAlimentadorIds(ids ?? []);
      } catch (e) {
        console.error(e);
        if (!cancelled) setAlimentadorIds([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedComuna]);

  const handleSetSelectedRegion = useCallback((id: string) => setSelectedRegion(id), []);
  const handleSetSelectedComuna = useCallback((id: string) => setSelectedComuna(id), []);

  return {
    regiones,
    comunas,
    selectedRegion,
    selectedComuna,
    localMapCenter,
    alimentadorIds,
    loading,
    setSelectedRegion: handleSetSelectedRegion,
    setSelectedComuna: handleSetSelectedComuna
  };
}
